import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import trainingPeaksAppIcon from 'media/integrations/training_peaks/TrainingPeaks_App_Icon.jpg'

import UserProfilePicture from 'components/User/UserProfilePicture'
import OutlineButton from 'components/Buttons/OutlineButton'

export default function CoachProfileListItem({ coach }) {

    const { ShowModal } = useContext(ModalContext)

    const userType = useSelector((state) => state.user.type)

    const canChangeRoles = ff(flags.canChangeRoles, { userType: userType })

    const GetRoleName = (role) => {
        if (role === null) return 'No Role'

        switch (role) {
            case 'super-admin': return 'Super Admin'
            case 'organization-owner': return 'Organization Owner'
            case 'organization-admin': return 'Organization Admin'
            case 'trainer': return 'Coach'
            default: return role
        }
    }

    const ShowRoleAssignmentModal = () => {

        if (canChangeRoles === false) {
            const modalData = {
                key: ModalKey.ActionWarning,
                fullscreen: false,
                size: 'quarter',
                titleText: 'Unable to change role',
                bodyText: [
                    'You do not have permission to change this role.',
                    'Reach out to your organization admin.',
                    'For more support please contact the support team at support@kinesisintegrated.com.'
                ],
                buttonText: 'Close',
                buttonAction: () => {}
            }

            ShowModal(modalData)

            return
        }

        if (coach.type === 'super-admin') {
            const modalData = {
                key: ModalKey.ActionWarning,
                fullscreen: false,
                size: 'quarter',
                titleText: 'Unable to change role',
                bodyText: [
                    'Super admin role cannot be changed on this screen.',
                    'For more support please contact the support team at support@kinesisintegrated.com.'
                ],
                buttonText: 'Close',
                buttonAction: () => {}
            }

            ShowModal(modalData)

            return
        }

        if (coach.type === 'organization-owner') {
            const modalData = {
                key: ModalKey.ActionWarning,
                fullscreen: false,
                size: 'quarter',
                titleText: 'Unable to change role',
                bodyText: [
                    'The organization owner cannot be changed on this screen.',
                    'There can only be one organization owner per organization.',
                    'If you need to change the organization owner, you can request an organization owner transfer by contacting the support team at support@kinesisintegrated.com.'
                ],
                buttonText: 'Close',
                buttonAction: () => {}
            }

            ShowModal(modalData)

            return
        }

        const modalData = {
            key: ModalKey.AssignRole,
            fullscreen: false,
            size: 'third',
            coach: coach
        }

        ShowModal(modalData)
    }

    return (
        <li className='w-full flex items-center px-6 py-4 gap-x-6'>
            <UserProfilePicture firstName={coach.firstName} lastName={coach.lastName} shadow={false} />
            
            <div className="flex-grow">
                <p className="font-lg font-semibold">{coach.firstName} {coach.lastName}</p>
                
                {coach.email.length > 0 && <p className="text-sm text-gray-600">{coach.email}</p>}
                {coach.email.length === 0 && <p className="text-sm text-gray-600">No Email</p>}
            </div>
            
            {coach.isTrainingPeaksIntegrationActive &&
                <div 
                    className="relative group"
                    onClick={(e) => { e.stopPropagation(); }} // TODO: Open TrainingPeaks Athlete Calendar in new tab
                >
                    <img 
                        src={trainingPeaksAppIcon}
                        alt="TrainingPeaks App Icon" 
                        className="h-8 w-8 rounded-md" 
                    />

                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                        TrainingPeaks account connected
                    </div>
                </div>
            }

            {canChangeRoles && (
                <div className='w-72 relative'>
                    <OutlineButton
                        text={GetRoleName(coach.type)}
                        className='hover:bg-gray-50'
                        action={ShowRoleAssignmentModal}
                    />

                    <ChevronUpDownIcon 
                        className="absolute top-2.5 right-3 text-gray-400 h-5 w-5" 
                        onClick={ShowRoleAssignmentModal}
                    />
                </div>
            )}

            {!canChangeRoles && (
                <div className='w-72 relative'>
                    <p className='w-full h-10 flex items-center justify-center gap-x-2 rounded-2xl text-sm text-center text-sm text-gray-600 bg-gray-100'>
                        {GetRoleName(coach.type)}
                    </p>
                </div>
            )}
        </li>
    )
}